import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { CustomUser } from 'api/models/CustomUser';
import { UserProfile } from 'api/models/UserProfile';
import { ApiService } from 'api/services/ApiService';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { userSearchQueryKey, userSearchStaleTime } from '../ModelStatus.constants';
import { AssignUsersDialogProps } from './AssignUsersDialog.types';

const AssignUsersDialog: React.FC<AssignUsersDialogProps> = ({
  open,
  onClose,
  onSave,
  fulfillmentData,
  title,
  availableUsers,
  getUserDisplayName,
  anchorEl,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [selectedReviewers, setSelectedReviewers] = useState<UserProfile[]>([]);
  const [selectedPreparers, setSelectedPreparers] = useState<UserProfile[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const popoverId = open ? 'assign-users-popover' : undefined;

  const { data: searchUsers = [], isLoading: isSearching } = useQuery({
    queryKey: [userSearchQueryKey, debouncedSearchTerm],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(
        debouncedSearchTerm.length > 2 ? debouncedSearchTerm : undefined
      );
      const users = (response as CustomUser[]) || [];
      return users;
    },
    enabled: open && debouncedSearchTerm.length > 2,
    staleTime: userSearchStaleTime,
  });

  const allAvailableUsers = useMemo(() => {
    const reviewers = availableUsers?.reviewers || [];
    const preparers = availableUsers?.preparers || [];
    const assignedUsers = [...reviewers, ...preparers];
    // searchUsers already filtered by backend to only include staff users
    const searchStaffUsers = searchUsers;
    
    // Combine and deduplicate users
    return [...assignedUsers, ...searchStaffUsers];
  }, [availableUsers, searchUsers]);

  // Filter available users based on search term
  // Note: Staff filtering is now done by the backend API
  const filteredUsers = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length < 3) {
      // Show all available users when not searching (they're already filtered to staff by backend)
      return allAvailableUsers;
    }
      
    // Filter by search term
    const searchLower = debouncedSearchTerm.toLowerCase();
    return allAvailableUsers.filter(user => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = user.email?.toLowerCase() || '';
      
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [allAvailableUsers, debouncedSearchTerm]);

  useEffect(() => {
    if (fulfillmentData && open) {
      const reviewers = fulfillmentData.reviewers as UserProfile[];
      const preparers = fulfillmentData.preparers as UserProfile[];
      
      setSelectedReviewers(reviewers);
      setSelectedPreparers(preparers);
    }
  }, [fulfillmentData, open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedReviewers([]);
      setSelectedPreparers([]);
      setIsSaving(false);
    }
  }, [open]);

  const handleReviewerToggle = useCallback((user: UserProfile) => {
    setSelectedReviewers(prev => (
      prev.includes(user)
        ? prev.filter(u => u !== user)
        : [...prev, user]
    ));
  }, []);

  const handlePreparerToggle = useCallback((user: UserProfile) => {
    setSelectedPreparers(prev => (
      prev.includes(user)
        ? prev.filter(u => u !== user)
        : [...prev, user]
    ));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(selectedReviewers, selectedPreparers);
      onClose();
    } catch {
      enqueueSnackbar('Failed to assign users. Please try again.', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedReviewers, selectedPreparers, onSave, onClose]);

  const getSelectedUserNames = (users: UserProfile[], role: string): string[] => 
    users
      .map(user => user ? getUserDisplayName(user) : `${role} ID: ${user.id}`);

  return (
    <Popover
      id={popoverId}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: { minWidth: 500, maxWidth: 600 }
        }
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <PersonIcon />
          <Typography variant="h6">{title}</Typography>
        </Box>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search staff members..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          helperText="Enter at least 3 characters to filter staff members."
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        {(selectedReviewers.length > 0 || selectedPreparers.length > 0) && (
          <Box sx={{ mb: 2 }}>
            {selectedReviewers.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Reviewers:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {getSelectedUserNames(selectedReviewers, 'Reviewer').map(name => (
                    <Chip key={name} label={name} size="small" color="primary" />
                  ))}
                </Box>
              </Box>
            )}
            {selectedPreparers.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Preparers:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {getSelectedUserNames(selectedPreparers, 'Preparer').map(name => (
                    <Chip key={name} label={name} size="small" color="secondary" />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
          {isSearching && (
            <ListItem>
              <ListItemText
                primary="Searching staff members..."
                secondary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Please wait</Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
          {!isSearching && filteredUsers.length === 0 && (
            <ListItem>
              <ListItemText
                primary={searchTerm ? 'No staff members found' : 'No staff members available'}
                secondary={searchTerm ? 'Try a different search term' : 'All available staff members are shown'}
              />
            </ListItem>
          )}
          {!isSearching && filteredUsers.length > 0 && (
            filteredUsers.map(user => (
              <ListItem key={user.id} divider secondaryAction={
                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" sx={{ mr: 1, minWidth: 60 }}>
                      Reviewer
                    </Typography>
                    <Checkbox
                      edge="end"
                      checked={selectedReviewers.includes(user)}
                      onChange={() => handleReviewerToggle(user)}
                      color="primary"
                    />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Typography variant="caption" sx={{ mr: 1, minWidth: 60 }}>
                      Preparer
                    </Typography>
                    <Checkbox
                      edge="end"
                      checked={selectedPreparers.includes(user)}
                      onChange={() => handlePreparerToggle(user)}
                      color="secondary"
                    />
                  </Box>
                </Box>
              }>
                <ListItemText
                  primary={getUserDisplayName(user as UserProfile)}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                          Staff Member
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isSaving ? 'Assigning...' : 'Assign Users'}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default AssignUsersDialog;
